import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  ResourceMatchType,
  ResourceSummaryDto,
  WorkingStepDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { saveBomItemResource } from '../store/asyncActions/saveBomItemResource'
import { ProjectEditableStateSelector } from '../store/selectors/ProjectEditableStateSelector'

type Props = {
  bomItemIds: string[]
  workingStep: WorkingStepDto
  onFocus?: () => void
  onBlur?: () => void
  disableLabel?: boolean
  disableUnderline?: boolean
  disableMatchDescription?: boolean
}

export const WorkingStepResourceSelector = ({
  bomItemIds,
  workingStep,
  onFocus,
  onBlur,
  disableLabel,
  disableUnderline,
  disableMatchDescription,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [open, setOpen] = useState(false)

  // const operationStatus = useAppSelector(
  //   projectOperationSelector(saveBomItemResource.typePrefix),
  //   isEqual
  // )

  const isEditable = useAppSelector(ProjectEditableStateSelector)

  const { partyId, projectId } = useOrganizationAndProjectContext()
  const { controller, loading, error } = useController(
    () => new BomItemController(partyId, projectId)
  )

  const [resourceId, setResourceId] = useState(workingStep.resource?.id)

  useEffect(() => {
    setResourceId(workingStep.resource?.id)
  }, [workingStep.resource?.id])

  const [resourceOptions, setResourceOptions] = useState<
    Array<ResourceSummaryDto>
  >([])

  useEffect(() => {
    if (open) {
      setResourceOptions([])
      controller
        .GetBomItemSelectableResources(
          bomItemIds[0],
          workingStep.primaryWorkingStep
        )
        .then((summaries) => {
          setResourceOptions(
            summaries.filter((x) => x.id !== workingStep.resource?.id)
          )
        })
    }
  }, [
    bomItemIds,
    controller,
    open,
    workingStep.primaryWorkingStep,
    workingStep.resource?.id,
  ])

  const handleChange = (e) => {
    dispatch(
      saveBomItemResource({
        bomItemIds,
        workingStepType: workingStep.primaryWorkingStep,
        resource: resourceOptions.find((x) => x.id === e.target.value),
        controllerInstance: controller,
      })
    )
  }

  return (
    <TextField
      select
      color="primary"
      fullWidth
      SelectProps={{
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        open: open,
        renderValue: () => (
          <Typography variant="caption">{workingStep.resource.name}</Typography>
        ),
      }}
      label={disableLabel ? null : t('project:resource')}
      error={Boolean(error[`get-selectable-resources-${bomItemIds[0]}`])}
      helperText={
        error[`get-selectable-resources-${bomItemIds[0]}`]?.message ||
        disableMatchDescription
          ? null
          : t(workingStep.resource?.matchDescription)
      }
      value={resourceId || ''}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={handleChange}
      disabled={
        loading[
          `set-resource-${bomItemIds[0]}-${workingStep.primaryWorkingStep}`
        ] ||
        workingStep.resource?.matchType === ResourceMatchType.OneCapableResource
      }
      size="small"
      className="resource-selector-input"
      InputProps={{
        disabled:
          !isEditable ||
          loading[
            `set-resource-${bomItemIds[0]}-${workingStep.primaryWorkingStep}`
          ],
        disableUnderline: disableUnderline,
        endAdornment: loading[
          `set-resource-${bomItemIds[0]}-${workingStep.primaryWorkingStep}`
        ] && (
          <InputAdornment position="end">
            <CircularProgress
              size={14}
              style={{ position: 'absolute', right: '18px' }}
            />
          </InputAdornment>
        ),
      }}
    >
      <MenuItem value={workingStep.resource?.id}>
        <Typography variant="caption">{workingStep.resource?.name}</Typography>
      </MenuItem>
      {loading[
        `get-selectable-resources-${bomItemIds[0]}-${workingStep.primaryWorkingStep}`
      ] && (
        <MenuItem>
          <Typography variant="caption">{`${t(
            'common:loading'
          )}...`}</Typography>
        </MenuItem>
      )}
      {resourceOptions.map((resource) => (
        <MenuItem key={resource.id} value={resource.id}>
          <Typography variant="caption">{resource.name}</Typography>
        </MenuItem>
      ))}
    </TextField>
  )
}
